// Arquivo: Social.tsx (botão Compartilhar SEMPRE abre modal de redes sociais, botões alinhados)
import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  Video,
  Mic,
  MicOff,
  StopCircle,
  Circle,
  CircleOff,
  Image,
  Mic as MicIcon,
  Upload,
  Share2,
  X as Close,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

declare global {
  interface Window {
    webkitSpeechRecognition: any;
    SpeechRecognition: any;
  }
}

const Social = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);

  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedVideos, setRecordedVideos] = useState<any[]>([]);
  const [capturedPhotos, setCapturedPhotos] = useState<any[]>([]);
  const [selectedMedia, setSelectedMedia] = useState<string | null>(null);
  const [isImage, setIsImage] = useState(false);

  // Modal de compartilhamento
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setIsCameraOn(true);
      toast.success('Câmera ativada!');
    } catch (err) {
      console.error(err);
      toast.error('Erro ao acessar câmera e microfone. Verifique as permissões.');
    }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    if (videoRef.current) videoRef.current.srcObject = null;
    setIsCameraOn(false);
  };

  const toggleMic = () => {
    if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach((t) => (t.enabled = !isMicOn));
      setIsMicOn(!isMicOn);
    }
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    recordedChunksRef.current = [];
    const recorder = new MediaRecorder(streamRef.current);

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) recordedChunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      const date = new Date().toLocaleString();
      setRecordedVideos((p) => [...p, { url, caption: '', date }]);
      setSelectedMedia(url);
      setIsImage(false);
    };

    recorder.start();
    mediaRecorderRef.current = recorder;
    setIsRecording(true);
    toast.success('Gravação iniciada.');
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
    toast.success('Gravação finalizada.');
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/png');
      const date = new Date().toLocaleDateString();
      setCapturedPhotos((p) => [...p, { url: dataUrl, caption: '', date }]);
      setSelectedMedia(dataUrl);
      setIsImage(true);
      toast.success('Foto capturada.');
    }
  };

  // Compartilhamento — sempre abre o modal personalizado
  const openShareModal = (url: string) => {
    setShareUrl(url);
    setShowShareModal(true);
  };
  const handleShare = (url: string) => {
    openShareModal(url);
  };
  const shareOptions = (url: string) => {
    const encodedUrl = encodeURIComponent(url);
    const text = encodeURIComponent('Confira esse conteúdo no RusticoPlay!');
    return [
      { name: 'WhatsApp', link: `https://wa.me/?text=${text}%20${encodedUrl}` },
      { name: 'Facebook', link: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}` },
      { name: 'Twitter / X', link: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${text}` },
    ];
  };
  const closeModal = () => setShowShareModal(false);

  const handleTranscribe = (idx: number, isPhoto = false) => {
    const SpeechRecognition = window.SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      return toast.error('Reconhecimento de voz não suportado neste navegador.');
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'pt-BR';
    recognition.interimResults = true;
    recognition.onresult = (e: any) => {
      const transcript = Array.from(e.results)
        .map((r: any) => r[0].transcript)
        .join('');
      if (isPhoto) {
        const upd = [...capturedPhotos];
        upd[idx].caption = transcript;
        setCapturedPhotos(upd);
      } else {
        const upd = [...recordedVideos];
        upd[idx].caption = transcript;
        setRecordedVideos(upd);
      }
    };
    recognition.onerror = (e: any) => toast.error('Erro no reconhecimento de voz: ' + e.error);
    recognition.start();
    toast.info('Escutando...');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('video/')) {
      const url = URL.createObjectURL(file);
      const date = new Date().toLocaleDateString();
      setRecordedVideos((p) => [...p, { url, caption: '', date }]);
      setSelectedMedia(url);
      setIsImage(false);
      toast.success('Vídeo adicionado com sucesso!');
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        const date = new Date().toLocaleDateString();
        setCapturedPhotos((p) => [...p, { url: dataUrl, caption: '', date }]);
        setSelectedMedia(dataUrl);
        setIsImage(true);
        toast.success('Imagem adicionada com sucesso!');
      };
      reader.readAsDataURL(file);
    }
  };

  // Deletar vídeos e imagens
  const handleDeleteVideo = (idx: number) => {
    const updated = [...recordedVideos];
    updated.splice(idx, 1);
    setRecordedVideos(updated);
    toast.info('Vídeo removido');
  };
  const handleDeletePhoto = (idx: number) => {
    const updated = [...capturedPhotos];
    updated.splice(idx, 1);
    setCapturedPhotos(updated);
    toast.info('Imagem removida');
  };

  // JSX
  return (
    <div className="min-h-screen bg-[#141414] text-white flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto px-4 pt-24 pb-10">
        <h1 className="text-3xl font-bold mb-6">Live Online</h1>

        {/* bloco câmera */}
        <div className="flex flex-col lg:flex-row gap-6 mb-10">
          <div className="flex-1 space-y-6">
            <div className="bg-black/70 p-4 rounded-lg relative">
              <div className="relative">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full aspect-[16/9] object-cover rounded bg-black"
                  style={{ height: '120%' }}
                />
                <img
                  src="/RusticoPlayer.svg"
                  alt="RusticoPlayer Logo"
                  className="absolute top-2 left-4 h-12 opacity-70"
                />
              </div>
              <canvas ref={canvasRef} className="hidden" />
              <div className="flex gap-3 justify-center mt-4">
                <Button onClick={startCamera} disabled={isCameraOn} variant="ghost" className="bg-[#222] text-white">
                  <Video size={18} /> Live
                </Button>
                <Button onClick={stopCamera} disabled={!isCameraOn} variant="ghost" className="bg-[#222] text-white">
                  <CircleOff size={18} /> Parar
                </Button>
                <Button onClick={toggleMic} disabled={!isCameraOn} variant="ghost" className="bg-[#222] text-white">
                  {isMicOn ? <Mic size={18} /> : <MicOff size={18} />}Microfone
                </Button>
                {!isRecording ? (
                  <Button onClick={startRecording} disabled={!isCameraOn} variant="ghost" className="bg-[#222] text-white">
                    <Circle size={18} />Gravar
                  </Button>
                ) : (
                  <Button onClick={stopRecording} variant="ghost" className="bg-[#222] text-white">
                    <StopCircle size={18} />
                  </Button>
                )}
                <Button onClick={capturePhoto} disabled={!isCameraOn} variant="ghost" className="bg-[#222] text-white">
                  <Image size={18} />Imagem
                </Button>
              </div>
            </div>
          </div>

          {/* bloco upload/preview */}
          <div className="flex-1 space-y-6">
            <div className="bg-black/70 p-6 rounded-lg">
              <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
                <div className="mb-4">
                  {selectedMedia ? (
                    isImage ? (
                      <img
                        src={selectedMedia!}
                        alt="Selecionado"
                        className="w-full aspect-video object-cover rounded"
                        style={{ height: '120%' }}
                      />
                    ) : (
                      <video src={selectedMedia!} controls className="w-full aspect-video rounded object-cover" />
                    )
                  ) : (
                    <div className="w-full aspect-video bg-black flex items-center justify-center rounded">
                      <img src="/RusticoPlayer.svg" alt="RusticoPlayer Logo" className="h-12 opacity-30" />
                    </div>
                  )}
                </div>
                <div className="flex gap-3">
                  <Button type="button" onClick={() => fileInputRef.current?.click()} variant="ghost" className="bg-[#222] text-white flex items-center gap-2">
                    <Video size={20} /> Vídeo
                  </Button>
                  <input ref={fileInputRef} type="file" accept="video/*" className="hidden" onChange={handleFileChange} />
                  <Button type="button" onClick={() => imageInputRef.current?.click()} variant="ghost" className="bg-[#222] text-white flex items-center gap-2">
                    <Upload size={18} /> Imagem
                  </Button>
                  <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Galerias ------------------------------------------------ */}
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* vídeos */}
          <div className="bg-black/60 p-4 rounded-lg">
            <h3 className="text-xl font-semibold mb-4">Vídeos</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {recordedVideos.map((item, idx) => (
                <div key={idx} className="relative">
                  <video
                    src={item.url}
                    className="w-full h-40 object-cover rounded cursor-pointer"
                    muted
                    onClick={() => {
                      setSelectedMedia(item.url);
                      setIsImage(false);
                    }}
                  />
                  <textarea
                    rows={1}
                    maxLength={100}
                    placeholder="Digite ou dite a legenda..."
                    value={item.caption}
                    onChange={(e) => {
                      const updated = [...recordedVideos];
                      updated[idx].caption = e.target.value;
                      setRecordedVideos(updated);
                    }}
                    className="w-full bg-[#1a1a1a] text-white text-sm resize-none rounded p-2 mt-2 outline-none placeholder-gray-400"
                  />
                  <div className="flex w-full gap-2 mt-2">
                    <Button size="sm" onClick={() => handleTranscribe(idx)} className="flex-1 bg-[#222] text-white">
                      <MicIcon size={14} /> Falar
                    </Button>
                    <Button size="sm" onClick={() => handleShare(item.url)} className="flex-1 bg-[#222] text-white">
                      <Share2 size={14} /> Compartilhar
                    </Button>
                    <Button size="sm" onClick={() => handleDeleteVideo(idx)} className="flex-1 bg-red-600 text-white">
                      <Trash2 size={14} /> Deletar
                    </Button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{item.date}</p>
                </div>
              ))}
            </div>
          </div>

          {/* imagens */}
          <div className="bg-black/60 p-4 rounded-lg">
            <h3 className="text-xl font-semibold mb-4">Imagens</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {capturedPhotos.map((item, idx) => (
                <div key={idx} className="relative">
                  <img
                    src={item.url}
                    alt={`Foto ${idx}`}
                    className="w-full h-40 object-cover rounded cursor-pointer"
                    onClick={() => {
                      setSelectedMedia(item.url);
                      setIsImage(true);
                    }}
                  />
                  <textarea
                    rows={1}
                    maxLength={100}
                    placeholder="Digite ou dite a legenda..."
                    value={item.caption}
                    onChange={(e) => {
                      const updated = [...capturedPhotos];
                      updated[idx].caption = e.target.value;
                      setCapturedPhotos(updated);
                    }}
                    className="w-full bg-[#1a1a1a] text-white text-sm resize-none rounded p-2 mt-2 outline-none placeholder-gray-400"
                  />
                  <div className="flex w-full gap-2 mt-2">
                    <Button size="sm" onClick={() => handleTranscribe(idx, true)} className="flex-1 bg-[#222] text-white">
                      <MicIcon size={14} /> Falar
                    </Button>
                    <Button size="sm" onClick={() => handleShare(item.url)} className="flex-1 bg-[#222] text-white">
                      <Share2 size={14} /> Compartilhar
                    </Button>
                    <Button size="sm" onClick={() => handleDeletePhoto(idx)} className="flex-1 bg-red-600 text-white">
                      <Trash2 size={14} /> Deletar
                    </Button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{item.date}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />

      {/* Modal de compartilhamento */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-[#222] p-6 rounded-lg w-72 relative">
            <button onClick={closeModal} className="absolute right-2 top-2 text-gray-400 hover:text-white">
              <Close size={18} />
            </button>
            <h4 className="text-lg font-semibold mb-4 text-center">Compartilhar em…</h4>
            <div className="space-y-2">
              {shareOptions(shareUrl).map((opt) => (
                <a
                  key={opt.name}
                  href={opt.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center py-2 rounded bg-[#333] hover:bg-[#444]"
                >
                  {opt.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Social;
